import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { MeFeedAuthor, MeFeedEntityType, MeHomeBootstrap, MeHomeBootstrapOptions } from './me';

const HOME_BOOTSTRAP_QUERY = `
  query HomeBootstrap(
    $limit: Int
    $entity: [FeedEntityType!]
    $author: FeedAuthor
    $projectIds: [ID!]
    $planStatus: [String!]
    $reportStatus: [String!]
    $postMortemStatus: [String!]
    $coActionStatus: [String!]
    $codeReviewStatus: [String!]
    $days: Int
    $tzOffsetMinutes: Int
  ) {
    homeBootstrap(
      limit: $limit
      entity: $entity
      author: $author
      projectIds: $projectIds
      planStatus: $planStatus
      reportStatus: $reportStatus
      postMortemStatus: $postMortemStatus
      coActionStatus: $coActionStatus
      codeReviewStatus: $codeReviewStatus
      days: $days
      tzOffsetMinutes: $tzOffsetMinutes
    )
  }
`;

type HomeBootstrapGraphqlVariables = {
  limit?: number;
  entity?: MeFeedEntityType[];
  author?: MeFeedAuthor;
  projectIds?: string[];
  planStatus?: string[];
  reportStatus?: string[];
  postMortemStatus?: string[];
  coActionStatus?: string[];
  codeReviewStatus?: string[];
  days?: number;
  tzOffsetMinutes?: number;
};

type HomeBootstrapGraphqlPayload = {
  data?: { homeBootstrap: MeHomeBootstrap | null } | null;
  errors?: Array<{ message: string }>;
};

const omitEmpty = (input: HomeBootstrapGraphqlVariables): HomeBootstrapGraphqlVariables => {
  const out: HomeBootstrapGraphqlVariables = {};
  for (const [key, value] of Object.entries(input) as Array<[keyof HomeBootstrapGraphqlVariables, unknown]>) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value as never;
  }
  return out;
};

export const fetchMeHomeGraphql = async (
  token: string,
  opts: MeHomeBootstrapOptions = {},
): Promise<MeHomeBootstrap> => {
  const variables = omitEmpty({
    limit: opts.limit,
    entity: opts.entity,
    author: opts.author,
    projectIds: opts.projectIds,
    planStatus: opts.planStatus,
    reportStatus: opts.reportStatus,
    postMortemStatus: opts.postMortemStatus,
    coActionStatus: opts.coActionStatus,
    codeReviewStatus: opts.codeReviewStatus,
    days: opts.days,
    tzOffsetMinutes: opts.tzOffsetMinutes,
  });

  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/graphql`, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({ query: HOME_BOOTSTRAP_QUERY, variables, operationName: 'HomeBootstrap' }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch home bootstrap: ${response.status}`);
  }

  const payload = (await response.json()) as HomeBootstrapGraphqlPayload;
  if (payload.errors && payload.errors.length > 0) {
    throw new Error(payload.errors[0]?.message ?? 'GraphQL home bootstrap failed');
  }
  const bootstrap = payload.data?.homeBootstrap;
  if (!bootstrap) {
    throw new Error('GraphQL home bootstrap returned no data');
  }
  return bootstrap;
};
